import { FormField } from './FormField';
import { FormSection } from './FormSection';
import type { ProjectFormValues } from '../../schemas/project.schema';
import { Input } from '@/shared/components/ui/input';
import { Select } from '@/shared/components/ui/select';
import { messages } from '@/shared/constants/messages';
import type { CatalogOption, WritableMinistry } from '@/shared/types/domain';

interface SectionIdentityProps {
  values: ProjectFormValues;
  ministries: WritableMinistry[];
  categories: CatalogOption[];
  errors: Partial<Record<keyof ProjectFormValues, string>>;
  /** Texto: debounce. */
  onChangeText: (patch: Partial<ProjectFormValues>) => void;
  /** Discreto (select): salva na hora. */
  onChangeNow: (patch: Partial<ProjectFormValues>) => void;
  /**
   * Trancado a partir do ENVIO, não a partir da criação.
   *
   * Enquanto o projeto é rascunho o banco aceita a troca
   * (`draft_ministry_is_editable`): ninguém de fora do ministério viu o
   * rascunho ainda, então mover não reescreve retroativamente quem podia
   * lê-lo. Depois do envio existem fila, decisão e linha do tempo, e os três
   * dizem "deste ministério" — ali a RPC recusa com `ministry_scope_violation`.
   */
  ministryLocked: boolean;
}

export function SectionIdentity({
  values,
  ministries,
  categories,
  errors,
  onChangeText,
  onChangeNow,
  ministryLocked,
}: SectionIdentityProps) {
  const selected = ministries.find((m) => m.ministry_id === values.ministry_id);
  // US-08 pede indicação clara de em nome de quem o projeto está sendo criado,
  // não um select de um item só: com um vínculo, o campo vira frase.
  const singleMinistry = ministries.length === 1 ? ministries[0] : undefined;

  return (
    <FormSection id="identificacao" number={1} title={messages.projectSections.identity}>
      {singleMinistry || ministryLocked ? (
        <p className="rounded-md bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">{messages.form.onBehalfOf}: </span>
          <span className="font-medium">
            {(selected ?? singleMinistry)?.ministry_name ?? '—'}
          </span>
        </p>
      ) : (
        <FormField
          label="Ministério responsável"
          required
          error={errors.ministry_id}
          hint="O projeto pertence a este ministério, e é a liderança dele que responde por ele."
        >
          <Select
            value={values.ministry_id ?? ''}
            onChange={(event) =>
              onChangeNow({ ministry_id: event.target.value || null })
            }
          >
            {/* O vazio existe só ENQUANTO não há escolha. Depois dela o
                projeto pode trocar de ministério, mas não voltar a não ter um:
                a coluna é NOT NULL, e a RPC responderia `ministry_required` a
                quem só queria reabrir a lista. */}
            {values.ministry_id ? null : <option value="">Selecione…</option>}
            {ministries.map((ministry) => (
              <option key={ministry.ministry_id} value={ministry.ministry_id}>
                {ministry.ministry_name}
              </option>
            ))}
          </Select>
        </FormField>
      )}

      <FormField label="Título do projeto" required error={errors.title}>
        <Input
          value={values.title ?? ''}
          maxLength={160}
          placeholder="Ex.: Conferência de Jovens 2026"
          onChange={(event) => onChangeText({ title: event.target.value || null })}
        />
      </FormField>

      <FormField label="Categoria" required error={errors.category_id}>
        <Select
          value={values.category_id ?? ''}
          onChange={(event) => onChangeNow({ category_id: event.target.value || null })}
        >
          <option value="">Selecione…</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
      </FormField>
    </FormSection>
  );
}
